import React, {useEffect, useState} from "react";
import propTypes from "prop-types";
import {
    PaginationBlockItemsWrapper,
    PaginationWrapper,
    PaginationWrapperA,
    PaginationWrapperButton,
    PaginationWrapperLi,
    PaginationWrapperUl
} from './Pagination.styles';

export const Pagination = ({...props}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageNeighbours] = useState(
        props.pageNeighbours ? Math.max(0, Math.min(props.pageNeighbours, 2)) : 0,
    );
    const pageLimit = props.pageLimit && props.pageLimit > 0 ? props.pageLimit : 10;
    const totalRecords = props.totalRecords ? props.totalRecords : 0;
    const totalPages = Math.ceil(totalRecords / pageLimit);

    const MORE_PAGE = '...';
    const MORE_LEFT_PAGE = 'LEFT_MOVE';
    const MORE_RIGHT_PAGE = 'RIGHT_MOVE';

    const range = (from, to, step = 1) => {
        let i = from;
        const range = [];

        while (i <= to) {
            range.push(i);
            i += step;
        }

        return range;
    };

    const fetchPageNumbers = () => {
        const inTotalPages = totalPages;
        const inCurrentPage = currentPage;
        const inPageNeighbours = pageNeighbours;

        /**
         * totalNumbers: the total page numbers to show on the control
         * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
         */
        const totalNumbers = pageNeighbours * 2 + 3;
        const totalBlocks = totalNumbers + 2;

        if (inTotalPages > totalBlocks) {
            const startPage = Math.max(2, inCurrentPage - inPageNeighbours);
            const endPage = Math.min(inTotalPages - 1, inCurrentPage + inPageNeighbours);
            let pages = range(startPage, endPage);

            /**
             * hasLeftSpill: has hidden pages to the left
             * hasRightSpill: has hidden pages to the right
             * spillOffset: number of hidden pages either to the left or to the right
             */
            const hasLeftSpill = startPage > 2;
            const hasRightSpill = inTotalPages - endPage > 1;
            const spillOffset = totalNumbers - (pages.length + 1);

            switch (true) {
                // handle: (1) < {5 6} [7] {8 9} (10)
                case hasLeftSpill && !hasRightSpill: {
                    const extraPages = range(startPage - spillOffset, startPage - 1);
                    pages = [MORE_LEFT_PAGE, ...extraPages, ...pages];
                    break;
                }

                // handle: (1) {2 3} [4] {5 6} > (10)
                case !hasLeftSpill && hasRightSpill: {
                    const extraPages = range(endPage + 1, endPage + spillOffset);
                    pages = [...pages, ...extraPages, MORE_RIGHT_PAGE];
                    break;
                }

                // handle: (1) < {4 5} [6] {7 8} > (10)
                case hasLeftSpill && hasRightSpill:
                default: {
                    pages = [MORE_LEFT_PAGE, ...pages, MORE_RIGHT_PAGE];
                    break;
                }
            }

            return [1, ...pages, inTotalPages];
        }

        return range(1, inTotalPages);
    };

    const gotoPage = (page) => {
        const {onPageChanged = (f) => f} = props;
        const currentPage = Math.max(0, Math.min(page, totalPages));
        const paginationData = {
            currentPage,
            totalPages: totalPages,
            pageLimit: props.pageLimit,
            totalRecords: totalRecords,
        };
        setCurrentPage(currentPage);
        onPageChanged(paginationData);
    };

    const handleClick = (page) => (evt) => {
        evt.preventDefault();
        gotoPage(page);
    };

    const handleMoveMoreLeft = (evt, pages, index) => {
        evt.preventDefault();
        gotoPage(pages[index + 1] - 1);
    };

    const handleMoveMoreRight = (evt, pages, index) => {
        evt.preventDefault();
        gotoPage(pages[index - 1] + 1);
    };

    const handleMoveLeft = (evt) => {
        evt.preventDefault();
        gotoPage(currentPage - 1);
    };

    const handleMoveRight = (evt) => {
        evt.preventDefault();
        gotoPage(currentPage + 1);
    };

    useEffect(() => {
        gotoPage(1);
    }, [props.pageLimit, totalPages]);

    if (!totalRecords || totalPages === 1) return null;

    const pages = fetchPageNumbers();
    return <PaginationWrapper>
        <PaginationBlockItemsWrapper>
            <PaginationWrapperButton
                aria-label="Previous"
                onClick={handleMoveLeft}
                disabled={currentPage === 1}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
                    <path
                        d="M2.90161 5.40674L7.96411 0.344238C8.28052 0.00146484 8.83423 0.00146484 9.15063 0.344238C9.49341 0.660645 9.49341 1.21436 9.15063 1.53076L4.69458 5.98682L9.15063 10.4692C9.49341 10.7856 9.49341 11.3394 9.15063 11.6558C8.83423 11.9985 8.28052 11.9985 7.96411 11.6558L2.90161 6.59326C2.55884 6.27686 2.55884 5.72314 2.90161 5.40674Z"
                        fill="currentColor"/>
                </svg>
            </PaginationWrapperButton>

            <PaginationWrapperUl>
                {pages.map((page, index) => {
                    if (page === MORE_LEFT_PAGE || page === MORE_RIGHT_PAGE)
                        return (
                            <PaginationWrapperLi key={index} className="page-item">
                                <PaginationWrapperA
                                    href="#"
                                    aria-label="Next"
                                    onClick={(event) => {
                                        page === MORE_LEFT_PAGE
                                            ? handleMoveMoreLeft(event, pages, index)
                                            : handleMoveMoreRight(event, pages, index);
                                    }}
                                >
                                    {MORE_PAGE}
                                </PaginationWrapperA>
                            </PaginationWrapperLi>
                        );
                    return (
                        <PaginationWrapperLi key={index}>                            
                            <PaginationWrapperA
                                activeStatus={currentPage === page}
                                href="#"
                                onClick={handleClick(page)}
                            >
                                {page}
                            </PaginationWrapperA>
                        </PaginationWrapperLi>
                    );
                })}
            </PaginationWrapperUl>
            <PaginationWrapperButton
                aria-label="Next"
                onClick={handleMoveRight}
                disabled={currentPage === totalPages}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
                    <path
                        d="M9.15064 6.59326L4.08813 11.6558C3.77173 11.9985 3.21802 11.9985 2.90161 11.6558C2.55884 11.3394 2.55884 10.7856 2.90161 10.4692L7.35767 6.01318L2.90161 1.53076C2.55884 1.21435 2.55884 0.660644 2.90161 0.344238C3.21802 0.0014643 3.77173 0.00146435 4.08814 0.344238L9.15064 5.40674C9.49341 5.72314 9.49341 6.27686 9.15064 6.59326Z"
                        fill="currentColor"/>
                </svg>
            </PaginationWrapperButton>
        </PaginationBlockItemsWrapper>
    </PaginationWrapper>
};

Pagination.propTypes = {
    totalRecords: propTypes.number,
    pageLimit: propTypes.number,
    pageNeighbours: propTypes.number,
    onPageChanged: propTypes.any,
}